import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Scale } from "lucide-react";
import { EnterpriseGate } from "@/components/enterprise-gate";
import { format } from "date-fns";
import { useBranch } from "@/context/branch";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function fmt(v: number) {
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function BSRow({ label, value, indent, bold }: { label: string; value: number; indent?: boolean; bold?: boolean }) {
  return (
    <div className={`flex justify-between py-2.5 border-b border-border/40 ${indent ? "pl-5" : ""}`}>
      <span className={`text-sm ${bold ? "font-bold" : ""}`}>{label}</span>
      <span className={`font-semibold ${bold ? "text-base" : ""}`}>{fmt(value)}</span>
    </div>
  );
}

function BalanceSheetContent() {
  const { activeBranchId } = useBranch();
  const { data, isLoading } = useQuery({
    queryKey: ["accounting-balance-sheet", activeBranchId],
    queryFn: async () => {
      const query = activeBranchId ? `?branchId=${activeBranchId}` : "";
      const res = await fetch(`${BASE}/api/accounting/balance-sheet${query}`, { credentials: "include" });
      return res.json();
    },
  });

  if (isLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const isBalanced = Math.abs((data?.totalAssets ?? 0) - (data?.totalLiabilities ?? 0) - (data?.equity ?? 0)) < 1;

  return (
    <div className="space-y-6">
      <div className="text-sm text-muted-foreground">
        As at: <span className="font-medium">{data?.asAt ? format(new Date(data.asAt), "MMMM d, yyyy") : "—"}</span>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Assets */}
        <Card className="border-t-4 border-t-blue-500">
          <CardHeader className="pb-2"><CardTitle className="text-base">Assets</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {data?.assets?.map((a: any) => (
              <BSRow key={a.label} label={a.label} value={a.amount} indent />
            ))}
            <div className="flex justify-between py-3 font-bold text-sm border-t-2 border-border mt-2">
              <span>Total Assets</span>
              <span className="text-blue-600">{fmt(data?.totalAssets ?? 0)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Liabilities */}
        <Card className="border-t-4 border-t-red-400">
          <CardHeader className="pb-2"><CardTitle className="text-base">Liabilities</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {data?.liabilities?.map((l: any) => (
              <BSRow key={l.label} label={l.label} value={l.amount} indent />
            ))}
            <div className="flex justify-between py-3 font-bold text-sm border-t-2 border-border mt-2">
              <span>Total Liabilities</span>
              <span className="text-red-500">{fmt(data?.totalLiabilities ?? 0)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Equity */}
        <Card className={`border-t-4 ${(data?.equity ?? 0) >= 0 ? "border-t-emerald-500" : "border-t-destructive"}`}>
          <CardHeader className="pb-2"><CardTitle className="text-base">Equity</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="py-3 border-b border-border/40">
              <div className="text-sm text-muted-foreground mb-1">Owner's Equity</div>
              <div className="text-sm text-muted-foreground">= Total Assets − Total Liabilities</div>
            </div>
            <div className="flex justify-between py-3 font-bold border-t-2 border-border mt-2">
              <span>Net Equity</span>
              <span className={(data?.equity ?? 0) >= 0 ? "text-emerald-600 text-lg" : "text-destructive text-lg"}>
                {fmt(data?.equity ?? 0)}
              </span>
            </div>
            {!isBalanced && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded p-2 mt-2">Note: This is a simplified view for retail operations.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="border rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Products in Inventory</div>
              <div className="text-xl font-bold">{data?.productCount ?? 0}</div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Inventory Value (at cost)</div>
              <div className="text-xl font-bold">{fmt(data?.inventoryValue ?? 0)}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AccountingBalanceSheet() {
  const { activeBranch } = useBranch();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Scale className="w-7 h-7 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Balance Sheet — {activeBranch?.name ?? "All Branches"}</h1>
          <p className="text-muted-foreground mt-0.5">Assets, liabilities, and equity for the selected location.</p>
        </div>
      </div>
      <EnterpriseGate feature="Balance Sheet" description="A full balance sheet view is available on the Enterprise plan.">
        <BalanceSheetContent />
      </EnterpriseGate>
    </div>
  );
}
